import { Slot } from '@radix-ui/react-slot';
import * as React from 'react';
import { ComponentProps, FocusEvent } from 'react';
import { cn } from 'v5/platform/dom/cn';
import { hideNumberInputSpinnersClassName } from 'v5/platform/dom/hideNumberInputSpinnersClassName';
import {
  findFocusableElement,
  getFocusableSelector
} from '../platform/dom/isFocusable';
import { useFocusWithin } from 'v5/platform/react/useFocusWithin';
import { Overwrite } from '../platform/typescript';
import {
  inputGroup2ClassName,
  type InputGroup2ChromeProps
} from './_common/input2Chrome';

export {
  inputGroup2ClassName,
  inputGroup2FieldChrome,
  inputGroup2Size,
  input2ToneVariantShell
} from './_common/input2Chrome';

export type InputGroup2Props = Overwrite<
  ComponentProps<'div'>,
  InputGroup2ChromeProps & {
    leftSlot?: React.ReactNode;
    rightSlot?: React.ReactNode;
    onBlur?: (e: FocusEvent) => void;
    onFocus?: (e: FocusEvent) => void;
  }
>;

export const InputGroup2 = React.forwardRef<HTMLDivElement, InputGroup2Props>(
  (
    {
      leftSlot,
      rightSlot,
      children,
      className,
      tone,
      variant,
      size,
      onFocus,
      onBlur,
      ...props
    },
    ref
  ) => {
    const { focusWithinProps } = useFocusWithin({
      onFocusWithin: onFocus,
      onBlurWithin: onBlur
    });

    return (
      <Slot {...focusWithinProps}>
        <div
          className={cn(
            inputGroup2ClassName({ tone, variant, size }),
            className
          )}
          ref={ref}
          onMouseDownCapture={e => {
            if (
              e.target instanceof Element &&
              e.target.closest(getFocusableSelector())
            ) {
              return;
            }
            findFocusableElement(e.currentTarget)?.focus();
            e.stopPropagation();
            e.preventDefault();
          }}
          {...props}
          {...focusWithinProps}
        >
          {leftSlot}
          {children}
          {rightSlot}
        </div>
      </Slot>
    );
  }
);
InputGroup2.displayName = 'InputGroup2';

export type InputGroup2InputProps = React.ComponentProps<'input'> & {
  highlightOnFocus?: boolean;
  asChild?: boolean;
};

export const InputGroup2Input = React.forwardRef<
  HTMLInputElement,
  InputGroup2InputProps
>(
  (
    { className, onFocus, readOnly, highlightOnFocus, asChild, type, ...props },
    ref
  ) => {
    const Comp = asChild ? Slot : 'input';

    return (
      <Comp
        data-element="input-group-input"
        tabIndex={readOnly ? -1 : props.tabIndex}
        className={cn(
          'w-full border-none bg-transparent outline-none placeholder:text-fg-muted',
          !asChild && type === 'number' && hideNumberInputSpinnersClassName,
          className
        )}
        ref={ref}
        readOnly={readOnly}
        aria-readonly={readOnly}
        {...(!asChild ? { type } : {})}
        {...props}
        onFocus={e => {
          if (highlightOnFocus) {
            e.target.select();
          }
          onFocus?.(e);
        }}
      />
    );
  }
);
InputGroup2Input.displayName = 'InputGroup2Input';

export const InputGroup2Slot = React.forwardRef<
  HTMLDivElement,
  React.ComponentProps<'div'>
>(({ className, children, ...props }, ref) => {
  return (
    <div
      data-element="input-group-slot"
      className={cn('flex items-center', className)}
      ref={ref}
      {...props}
    >
      {children}
    </div>
  );
});
InputGroup2Slot.displayName = 'InputGroup2Slot';

export const InputGroup2Button = React.forwardRef<
  HTMLButtonElement,
  React.ComponentProps<'button'> & { asChild?: boolean }
>(({ className, children, asChild, ...props }, ref) => {
  const Comp = asChild ? Slot : 'button';

  return (
    <Comp
      data-element="input-group-button"
      type="button"
      className={cn(
        'flex items-center not-disabled:xhover:bg-brand-900/5 focus-visible:bg-brand-900/7 transition-colors',
        className
      )}
      ref={ref}
      {...props}
    >
      {children}
    </Comp>
  );
});
InputGroup2Button.displayName = 'InputGroup2Button';
